import React from "react";
import { Button } from "./Button.jsx";
import { TaskDetailsPanel } from "./TaskDetailsPanel.jsx";
import { NO_SPINNER_CLASS, typedNumberInputProps } from "../utils/numberInput.js";

// Shared belief-elicitation page. Used by the pre-lobby "initial" and "confirm"
// steps and by the postPeerBelief stage, so every elicitation offers the same
// response space: same numeric input, same range, same confidence field.

export function countWords(text = "") {
  const trimmed = String(text || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

export function normalizePredictionValue(value, taskConfig = {}) {
  if (typeof taskConfig?.normalizePredictionValue === "function") {
    return taskConfig.normalizePredictionValue(value);
  }
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

export function formatPredictionValue(value, taskConfig = {}) {
  const normalized = normalizePredictionValue(value, taskConfig);
  if (!normalized) return "";
  if (typeof taskConfig?.formatPredictionDisplay === "function") {
    return taskConfig.formatPredictionDisplay(normalized);
  }
  const matchingOption = (taskConfig?.options || []).find(
    (option) => String(option.value) === normalized
  );
  return matchingOption?.label || normalized;
}

export function PredictionButtonPage({
  onPredictionSelect,
  onPredictionSubmit,
  selectedPrediction,
  title,
  description,
  options = [],
  predictionInput = null,
  predictionDisplayFormatter,
  questionText,
  introPrompt,
  caseNotice,
  DetailsComponent,
  showLoanDetails = true,
  // Render the task description collapsed behind a toggle instead of inline.
  // Used by every elicitation after the first one.
  collapsibleDetails = false,
  detailsToggle = {},
  detailsTitle = "Case details",
  buttonText = "Continue",
  rationale = "",
  onRationaleChange,
  requireRationale = false,
  minRationaleWords = 1,
  rationaleLabel = "Please explain your rationale (required)",
  rationalePlaceholder = "Write a detailed explanation of why you chose this prediction.",
  confidence = "",
  onConfidenceChange,
  requireConfidence = false,
  confidenceMin = 0,
  confidenceMax = 100,
  requireWholeNumberConfidence = false,
  confidenceLabel,
  confidencePlaceholder,
  confidenceHelpText,
  confidenceErrorText,
  secondaryConfidence = "",
  onSecondaryConfidenceChange,
  requireSecondaryConfidence = false,
  secondaryConfidenceMin = 0,
  secondaryConfidenceMax = 100,
  requireWholeNumberSecondaryConfidence = false,
  secondaryConfidenceLabel,
  secondaryConfidencePlaceholder,
  secondaryConfidenceHelpText,
  secondaryConfidenceErrorText,
  secondaryConfidenceCombinedMax,
  integrityNotice = "",
  integrityFieldPrefix = "prediction",
  children,
}) {
  // Collapsed on arrival: the complete description was already shown on the
  // first prediction page.
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const isNumericPredictionInput = predictionInput?.type === "number";
  const trimmedPrediction = String(selectedPrediction ?? "").trim();
  const parsedPrediction = trimmedPrediction === "" ? null : Number(trimmedPrediction);
  const parsedPredictionMin = Number(predictionInput?.min);
  const parsedPredictionMax = Number(predictionInput?.max);
  const predictionMin = Number.isFinite(parsedPredictionMin) ? parsedPredictionMin : 0;
  const predictionMax = Number.isFinite(parsedPredictionMax) ? parsedPredictionMax : 100;
  const requireWholeNumberPrediction = predictionInput?.requireWholeNumber === true;
  const predictionLabel =
    predictionInput?.label ||
    `Enter your answer (${predictionMin}-${predictionMax})`;
  const predictionPlaceholder =
    predictionInput?.placeholder || `${predictionMin}-${predictionMax}`;
  const predictionHelpText = predictionInput?.helpText || "";
  const predictionValidationText =
    predictionInput?.errorText ||
    (requireWholeNumberPrediction
      ? `Please enter a whole-number value from ${predictionMin} to ${predictionMax}.`
      : `Please enter a value from ${predictionMin} to ${predictionMax}.`);
  const predictionInRange =
    parsedPrediction !== null &&
    Number.isFinite(parsedPrediction) &&
    parsedPrediction >= predictionMin &&
    parsedPrediction <= predictionMax &&
    (!requireWholeNumberPrediction || Number.isInteger(parsedPrediction));
  const normalizedPrediction =
    isNumericPredictionInput && predictionInRange
      ? (requireWholeNumberPrediction
          ? String(Math.trunc(parsedPrediction))
          : String(parsedPrediction))
      : trimmedPrediction;
  const hasValidPrediction = isNumericPredictionInput
    ? predictionInRange
    : Boolean(selectedPrediction);
  const formattedPrediction =
    hasValidPrediction && typeof predictionDisplayFormatter === "function"
      ? predictionDisplayFormatter(normalizedPrediction)
      : normalizedPrediction;
  const trimmedRationale = (rationale || "").trim();
  const parsedMinimumWords = Number(minRationaleWords);
  const minimumWordCount =
    Number.isFinite(parsedMinimumWords) && parsedMinimumWords > 0
      ? Math.floor(parsedMinimumWords)
      : 1;
  const rationaleWordCount = countWords(trimmedRationale);
  const hasValidRationale = !requireRationale || rationaleWordCount >= minimumWordCount;
  const rationaleValidationMessage = "You need to write more.";
  const parsedConfidence = confidence === "" ? null : Number(confidence);
  const parsedConfidenceMin = Number(confidenceMin);
  const parsedConfidenceMax = Number(confidenceMax);
  const effectiveConfidenceMin = Number.isFinite(parsedConfidenceMin) ? parsedConfidenceMin : 0;
  const effectiveConfidenceMax = Number.isFinite(parsedConfidenceMax) ? parsedConfidenceMax : 100;
  const effectiveConfidenceLabel =
    confidenceLabel ||
    `How confident are you in this prediction? (${effectiveConfidenceMin}-${effectiveConfidenceMax}%)`;
  const effectiveConfidencePlaceholder =
    confidencePlaceholder || `${effectiveConfidenceMin}-${effectiveConfidenceMax}`;
  const effectiveConfidenceErrorText =
    confidenceErrorText ||
    (requireWholeNumberConfidence
      ? `Please enter a whole-number confidence between ${effectiveConfidenceMin} and ${effectiveConfidenceMax}.`
      : `Please enter a confidence between ${effectiveConfidenceMin} and ${effectiveConfidenceMax}.`);
  const confidenceInRange =
    parsedConfidence !== null &&
    Number.isFinite(parsedConfidence) &&
    parsedConfidence >= effectiveConfidenceMin &&
    parsedConfidence <= effectiveConfidenceMax &&
    (!requireWholeNumberConfidence || Number.isInteger(parsedConfidence));
  const hasValidConfidence = !requireConfidence || confidenceInRange;
  const parsedSecondaryConfidence =
    secondaryConfidence === "" ? null : Number(secondaryConfidence);
  const parsedSecondaryConfidenceMin = Number(secondaryConfidenceMin);
  const parsedSecondaryConfidenceMax = Number(secondaryConfidenceMax);
  const effectiveSecondaryConfidenceMin = Number.isFinite(parsedSecondaryConfidenceMin)
    ? parsedSecondaryConfidenceMin
    : 0;
  const effectiveSecondaryConfidenceMax = Number.isFinite(parsedSecondaryConfidenceMax)
    ? parsedSecondaryConfidenceMax
    : 100;
  const effectiveSecondaryConfidenceLabel =
    secondaryConfidenceLabel ||
    `Enter the confidence for your initial answer (${effectiveSecondaryConfidenceMin}-${effectiveSecondaryConfidenceMax}%)`;
  const effectiveSecondaryConfidencePlaceholder =
    secondaryConfidencePlaceholder ||
    `${effectiveSecondaryConfidenceMin}-${effectiveSecondaryConfidenceMax}`;
  const secondaryConfidenceInRange =
    parsedSecondaryConfidence !== null &&
    Number.isFinite(parsedSecondaryConfidence) &&
    parsedSecondaryConfidence >= effectiveSecondaryConfidenceMin &&
    parsedSecondaryConfidence <= effectiveSecondaryConfidenceMax &&
    (!requireWholeNumberSecondaryConfidence ||
      Number.isInteger(parsedSecondaryConfidence));
  const parsedCombinedConfidenceMax = Number(secondaryConfidenceCombinedMax);
  const hasCombinedConfidenceCap = Number.isFinite(parsedCombinedConfidenceMax);
  const secondaryConfidenceWithinCombinedCap =
    !requireSecondaryConfidence ||
    !hasCombinedConfidenceCap ||
    (confidenceInRange &&
      secondaryConfidenceInRange &&
      parsedConfidence + parsedSecondaryConfidence <= parsedCombinedConfidenceMax);
  const defaultSecondaryConfidenceErrorText = requireWholeNumberSecondaryConfidence
    ? `Please enter a whole-number confidence between ${effectiveSecondaryConfidenceMin} and ${effectiveSecondaryConfidenceMax}.`
    : `Please enter a confidence between ${effectiveSecondaryConfidenceMin} and ${effectiveSecondaryConfidenceMax}.`;
  const combinedConfidenceErrorText = hasCombinedConfidenceCap
    ? `The two confidence values must sum to ${parsedCombinedConfidenceMax} or less.`
    : defaultSecondaryConfidenceErrorText;
  const effectiveSecondaryConfidenceErrorText =
    secondaryConfidenceErrorText ||
    (secondaryConfidenceWithinCombinedCap
      ? defaultSecondaryConfidenceErrorText
      : combinedConfidenceErrorText);
  const hasValidSecondaryConfidence =
    !requireSecondaryConfidence ||
    (secondaryConfidenceInRange && secondaryConfidenceWithinCombinedCap);
  const disabledReasons = [];
  if (!hasValidPrediction) {
    disabledReasons.push(
      isNumericPredictionInput
        ? predictionValidationText
        : "Please select your prediction."
    );
  }
  if (requireRationale && !hasValidRationale) {
    disabledReasons.push(rationaleValidationMessage);
  }
  if (requireConfidence && !hasValidConfidence) {
    disabledReasons.push(effectiveConfidenceErrorText);
  }
  if (requireSecondaryConfidence && !hasValidSecondaryConfidence) {
    disabledReasons.push(effectiveSecondaryConfidenceErrorText);
  }
  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>

      {integrityNotice && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-900">{integrityNotice}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        {description && <p className="text-xl mb-4">{description}</p>}
        {introPrompt && <p className="text-lg mb-4">{introPrompt}</p>}
        {caseNotice && (
          <p className="text-lg mb-4">
            <strong>{caseNotice}</strong>
          </p>
        )}

        {/* Three modes. The first prediction page shows the complete task
            description outright. Every later elicitation passes
            collapsibleDetails, which hides it behind a one-click toggle so the
            participant can always reread the task without it dominating the
            page. showLoanDetails={false} with no collapsibleDetails leaves the
            description off the page entirely, which now only happens in the
            conditions that never carried it. */}
        {showLoanDetails && DetailsComponent ? <DetailsComponent /> : null}
        {!showLoanDetails && collapsibleDetails && DetailsComponent ? (
          <div className="mt-4">
            <TaskDetailsPanel
              taskConfig={{ DetailsComponent, detailsToggle, detailsTitle }}
              open={detailsOpen}
              onToggle={() => setDetailsOpen((prev) => !prev)}
              scrollable
            />
          </div>
        ) : null}
      </div>

      {children}

      <p className="text-xl mb-6">{questionText || "What is your belief?"}</p>

      {isNumericPredictionInput ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            {predictionLabel}
          </label>
          <input
            className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
            type="number"
            data-integrity-field={`${integrityFieldPrefix}-prediction`}
            min={predictionMin}
            max={predictionMax}
            step={predictionInput?.step || (requireWholeNumberPrediction ? "1" : "any")}
            placeholder={predictionPlaceholder}
            value={selectedPrediction ?? ""}
            onChange={(e) => onPredictionSelect(e.target.value)}
            {...typedNumberInputProps()}
          />
          {predictionHelpText && (
            <p className="text-sm text-gray-600 mt-2">{predictionHelpText}</p>
          )}
          {!hasValidPrediction && trimmedPrediction && (
            <p className="text-sm text-gray-500 mt-2">
              {predictionValidationText}
            </p>
          )}
        </div>
      ) : (
        <div className="flex justify-center gap-4 mb-6">
          {options.map((option) => {
            const label = option.buttonLabel || option.label || option.value;
            const isSelected = selectedPrediction === option.value;
            return (
              <Button
                key={option.value}
                className={`px-8 py-4 ${isSelected ? "bg-blue-700" : "bg-blue-500 hover:bg-blue-600"}`}
                handleClick={() => onPredictionSelect(option.value)}
              >
                {label}
              </Button>
            );
          })}
        </div>
      )}

      {hasValidPrediction && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
          <p className="text-lg">
            {isNumericPredictionInput ? "You entered: " : "You selected: "}
            <strong className="text-blue-700">{formattedPrediction}</strong>
          </p>
        </div>
      )}

      {typeof onRationaleChange === "function" && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            {rationaleLabel}
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-md p-3 min-h-[120px]"
            data-integrity-field={`${integrityFieldPrefix}-rationale`}
            placeholder={rationalePlaceholder}
            value={rationale}
            onChange={(e) => onRationaleChange(e.target.value)}
          />
          {requireRationale && !hasValidRationale && (
            <p className="text-sm text-gray-500 mt-2">
              {rationaleValidationMessage}
            </p>
          )}
        </div>
      )}

      {typeof onConfidenceChange === "function" && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            {effectiveConfidenceLabel}
          </label>
          <input
            className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
            type="number"
            data-integrity-field={`${integrityFieldPrefix}-confidence`}
            min={effectiveConfidenceMin}
            max={effectiveConfidenceMax}
            step={requireWholeNumberConfidence ? "1" : "any"}
            placeholder={effectiveConfidencePlaceholder}
            value={confidence}
            onChange={(e) => onConfidenceChange(e.target.value)}
            {...typedNumberInputProps()}
          />
          {confidenceHelpText && (
            <p className="text-sm text-gray-600 mt-2">{confidenceHelpText}</p>
          )}
          {requireConfidence && !confidenceInRange && (
            <p className="text-sm text-gray-500 mt-2">
              {effectiveConfidenceErrorText}
            </p>
          )}
        </div>
      )}

      {typeof onSecondaryConfidenceChange === "function" && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-lg font-semibold mb-2">
            {effectiveSecondaryConfidenceLabel}
          </label>
          <input
            className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
            type="number"
            data-integrity-field={`${integrityFieldPrefix}-initial-answer-confidence`}
            min={effectiveSecondaryConfidenceMin}
            max={effectiveSecondaryConfidenceMax}
            step={requireWholeNumberSecondaryConfidence ? "1" : "any"}
            placeholder={effectiveSecondaryConfidencePlaceholder}
            value={secondaryConfidence}
            onChange={(e) => onSecondaryConfidenceChange(e.target.value)}
            {...typedNumberInputProps()}
          />
          {secondaryConfidenceHelpText && (
            <p className="text-sm text-gray-600 mt-2">{secondaryConfidenceHelpText}</p>
          )}
          {requireSecondaryConfidence && !hasValidSecondaryConfidence && (
            <p className="text-sm text-gray-500 mt-2">
              {effectiveSecondaryConfidenceErrorText}
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={() => {
              if (!hasValidPrediction) {
                alert(
                  isNumericPredictionInput
                    ? predictionValidationText
                    : "Please select your prediction first."
                );
                return;
              }
              if (requireRationale && !hasValidRationale) {
                alert(rationaleValidationMessage);
                return;
              }
              if (requireConfidence && !confidenceInRange) {
                alert(effectiveConfidenceErrorText);
                return;
              }
              if (requireSecondaryConfidence && !hasValidSecondaryConfidence) {
                alert(effectiveSecondaryConfidenceErrorText);
                return;
              }
              onPredictionSubmit(
                normalizedPrediction,
                trimmedRationale,
                parsedConfidence,
                parsedSecondaryConfidence
              );
            }}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={
              !hasValidPrediction ||
              !hasValidRationale ||
              !hasValidConfidence ||
              !hasValidSecondaryConfidence
            }
          >
            {buttonText}
          </Button>
        </div>
        {disabledReasons.length > 0 && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            {disabledReasons.join(" ")}
          </p>
        )}
      </div>
    </div>
  );
}
